// /admin — the site administration portal. The invite-code section.
//
// One of three sections; the others are /admin/groups and /admin/users, reached
// through the shared subnav in adminNav.ts.
//
// Ported from the PHP admin page. The one capability here that exists nowhere
// else is minting a SITE-WIDE code: one with no group scope, which lets a
// stranger register without joining anything. A group admin is confined to their
// own group's codes by the group-level screen.
//
// Registration requires a valid code whenever REQUIRE_INVITE_CODE is on, so this
// page is the front door's key cabinet. An empty table means nobody new can sign
// up, which is why the empty state says so rather than just "no codes".

import { ApiClientError, adminCreateInviteCode, adminFetchInviteCodes, adminSetInviteCodeActive } from '../api/client.js';
import { navigate, type View } from '../router.js';
import { el } from '../dom.js';
import {
  TimeFormat,
  fmtInstant,
  type AdminGroupOption,
  type AdminInviteCode,
  type InviteCodeStatus,
} from '@msc/shared';
import { viewerZone } from '../viewerZone.js';
import { adminError, adminSubnav } from './adminNav.js';

/** Badge text and class per derived status. The server decides which applies. */
const STATUS: Record<InviteCodeStatus, { label: string; cls: string }> = {
  active: { label: 'Active', cls: 'badge badge-success' },
  revoked: { label: 'Revoked', cls: 'badge badge-danger' },
  expired: { label: 'Expired', cls: 'badge badge-warning' },
  used_up: { label: 'Used up', cls: 'badge badge-warning' },
};

export const adminView: View = async (outlet) => {
  const main = el('main', { class: 'page' }, [el('p', { class: 'muted' }, 'Loading')]);
  outlet.append(main);

  let codes: AdminInviteCode[];
  let groups: AdminGroupOption[];
  let zone: string;
  try {
    const [data, viewer] = await Promise.all([adminFetchInviteCodes(), viewerZone()]);
    codes = data.codes;
    groups = data.groups;
    zone = viewer;
  } catch (err) {
    if (err instanceof ApiClientError && err.status === 401) {
      navigate('/login', true);
      return;
    }
    main.replaceChildren(
      el('div', { class: 'page-head' }, [el('h1', {}, 'Admin')]),
      adminSubnav('codes'),
      ...adminError(err instanceof ApiClientError ? err.status : undefined),
    );
    return;
  }

  const tableHost = el('div', {});
  const renderTable = (rows: AdminInviteCode[]): void => {
    tableHost.replaceChildren(rows.length ? codeTable(rows, zone) : emptyCodes());
  };
  renderTable(codes);

  main.replaceChildren(
    el('div', { class: 'page-head' }, [el('h1', {}, 'Admin')]),
    adminSubnav('codes'),
    el('h2', {}, 'Invite codes'),
    el(
      'p',
      { class: 'muted small' },
      'Registration requires a valid invite code. Site-wide codes let anyone sign up; ' +
        'group-scoped codes also auto-join the new member to that group.',
    ),
    createForm(groups, (created) => {
      codes = [created, ...codes];
      renderTable(codes);
    }),
    tableHost,
  );
};

function emptyCodes(): HTMLElement {
  return el('p', { class: 'muted' }, 'No invite codes yet. Nobody can register until you make one.');
}

function codeTable(rows: AdminInviteCode[], zone: string): HTMLElement {
  const body = el(
    'tbody',
    {},
    rows.map((c) => codeRow(c, zone)),
  );

  return el('div', { class: 'table-scroll' }, [
    el('table', { class: 'member-table' }, [
      el('thead', {}, [
        el('tr', {}, [
          el('th', {}, 'Code'),
          el('th', {}, 'Scope'),
          el('th', {}, 'Uses'),
          el('th', {}, 'Expires'),
          el('th', {}, 'Status'),
          el('th', {}, 'Created'),
          el('th', {}, ''),
        ]),
      ]),
      body,
    ]),
  ]);
}

/**
 * A copy-to-clipboard control for one code.
 *
 * The whole job of this page is handing a code to a person, and hand-selecting
 * eight ambiguous characters is where transcription errors come from.
 *
 * `navigator.clipboard` needs a secure context. localhost counts as one and
 * production is HTTPS, so it is available in both — but a plain-HTTP staging host
 * would leave it undefined, hence the guard. The button reports what actually
 * happened rather than always claiming success.
 */
function copyButton(code: string): HTMLElement {
  const btn = el('button', { type: 'button', class: 'btn-link btn-copy' }, 'copy');

  btn.addEventListener('click', () => {
    const done = (label: string): void => {
      btn.textContent = label;
      window.setTimeout(() => {
        btn.textContent = 'copy';
      }, 1500);
    };

    if (!navigator.clipboard) {
      done('unavailable');
      return;
    }
    void navigator.clipboard
      .writeText(code)
      .then(() => done('copied'))
      .catch(() => done('failed'));
  });

  return btn;
}

function codeRow(code: AdminInviteCode, zone: string): HTMLElement {
  const statusCell = el('td', {}, [
    el('span', { class: STATUS[code.status].cls }, STATUS[code.status].label),
  ]);

  const toggle = el('button', { type: 'button', class: 'btn btn-sm' }, '');
  const error = el('p', { class: 'form-error small', hidden: true });

  const paint = (c: AdminInviteCode): void => {
    toggle.textContent = c.isActive ? 'Revoke' : 'Restore';
    statusCell.replaceChildren(el('span', { class: STATUS[c.status].cls }, STATUS[c.status].label));
  };
  paint(code);

  let current = code;
  toggle.addEventListener('click', () => {
    toggle.setAttribute('disabled', '');
    error.hidden = true;
    void adminSetInviteCodeActive(current.codeId, !current.isActive)
      .then((updated) => {
        // Repaint from the SERVER's row, not from a locally flipped flag: the
        // status is derived, so a restored-but-expired code must come back
        // reading 'Expired' rather than 'Active'.
        current = updated;
        paint(updated);
      })
      .catch((err: unknown) => {
        error.textContent =
          err instanceof ApiClientError ? err.message : 'Could not update that code.';
        error.hidden = false;
      })
      .finally(() => toggle.removeAttribute('disabled'));
  });

  return el('tr', {}, [
    el('td', {}, [
      el('code', {}, code.code),
      ' ',
      copyButton(code.code),
      ...(code.note ? [el('div', { class: 'muted small' }, code.note)] : []),
      error,
    ]),
    el('td', {}, scopeCell(code)),
    // '∞' rather than a blank: an unlimited code and a code with an unknown cap
    // must not look the same.
    el('td', {}, `${code.useCount} / ${code.maxUses === null ? '∞' : code.maxUses}`),
    // expiresOn, NOT expiresAt sliced — the stored instant is end-of-day in the
    // SITE zone, so its UTC date is frequently the following day. See the field's
    // note in shared/src/admin.ts.
    el('td', { class: 'small' }, code.expiresOn ?? 'Never'),
    statusCell,
    // created_at is a true instant with no authored-date meaning, so unlike the
    // expiry it renders in the VIEWER's zone like every other instant on the site.
    el('td', { class: 'muted small' }, fmtInstant(code.createdAt, zone, TimeFormat.SQL_DATE)),
    el('td', {}, [toggle]),
  ]);
}

/**
 * The Scope cell: site-wide, a link to the group, or a dead group.
 *
 * A null `groupName` with a non-null `groupId` means the group is gone — not
 * soft-deleted but actually absent, which the phase smoke suites produce by
 * hard-deleting their fixtures. Linking there would hand the admin a 404, so the
 * orphan is flagged as plain text instead. It still has to be VISIBLE: a code
 * scoped to a group that no longer exists can never be redeemed, and finding
 * those to revoke them is precisely what this table is for.
 */
function scopeCell(code: AdminInviteCode): HTMLElement | string {
  if (code.groupId === null) return 'Site-wide';
  if (code.groupName === null) {
    return el(
      'span',
      { class: 'muted', title: 'This group no longer exists, so the code cannot be redeemed.' },
      `Group #${code.groupId} (deleted)`,
    );
  }
  return el('a', { href: `/groups/${code.groupId}` }, code.groupName);
}

function createForm(
  groups: AdminGroupOption[],
  onCreated: (code: AdminInviteCode) => void,
): HTMLElement {
  const error = el('p', { class: 'form-error', hidden: true });
  const submit = el('button', { type: 'submit', class: 'btn' }, 'Create code');

  const codeInput = el('input', {
    id: 'nc-code',
    name: 'code',
    type: 'text',
    maxlength: 64,
    placeholder: 'leave blank to auto-generate',
    class: 'form-control',
  }) as HTMLInputElement;

  const scope = el('select', { id: 'nc-scope', name: 'groupId', class: 'form-select' }, [
    el('option', { value: '' }, 'Site-wide (no group)'),
    ...groups.map((g) => el('option', { value: String(g.groupId) }, g.name)),
  ]) as HTMLSelectElement;

  const maxUses = el('input', {
    id: 'nc-max',
    name: 'maxUses',
    type: 'number',
    min: 1,
    step: 1,
    placeholder: 'unlimited',
    class: 'form-control',
  }) as HTMLInputElement;

  const expires = el('input', {
    id: 'nc-expires',
    name: 'expires',
    type: 'date',
    class: 'form-control',
  }) as HTMLInputElement;

  const note = el('input', {
    id: 'nc-note',
    name: 'note',
    type: 'text',
    maxlength: 255,
    placeholder: 'what this code is for',
    class: 'form-control',
  }) as HTMLInputElement;

  // method="post" even though script submits it: if the script ever fails, the
  // browser must not fall back to GET. Hard rule, see CLAUDE.md.
  const form = el('form', { method: 'post', class: 'card' }, [
    el('div', { class: 'form-grid' }, [
      el('div', { class: 'form-group' }, [
        el('label', { class: 'form-label', for: 'nc-code' }, 'Code'),
        codeInput,
      ]),
      el('div', { class: 'form-group' }, [
        el('label', { class: 'form-label', for: 'nc-scope' }, 'Scope'),
        scope,
      ]),
      el('div', { class: 'form-group' }, [
        el('label', { class: 'form-label', for: 'nc-max' }, 'Max uses'),
        maxUses,
      ]),
      el('div', { class: 'form-group' }, [
        el('label', { class: 'form-label', for: 'nc-expires' }, 'Expires'),
        expires,
      ]),
      el('div', { class: 'form-group' }, [
        el('label', { class: 'form-label', for: 'nc-note' }, 'Note'),
        note,
      ]),
    ]),
    error,
    submit,
  ]) as HTMLFormElement;

  form.addEventListener('submit', (ev) => {
    ev.preventDefault();
    submit.setAttribute('disabled', '');
    error.hidden = true;

    // Omit every empty field rather than sending '' — the server treats absent
    // as "site-wide" / "unlimited" / "never", and '' would fail validation.
    const body: Record<string, unknown> = {};
    if (codeInput.value.trim()) body.code = codeInput.value.trim();
    if (scope.value) body.groupId = Number(scope.value);
    if (maxUses.value) body.maxUses = Number(maxUses.value);
    if (expires.value) body.expires = expires.value;
    if (note.value.trim()) body.note = note.value.trim();

    // Captured BEFORE form.reset() clears it. This is the site-local date the
    // admin actually typed, which is precisely what `expiresOn` means — no need
    // to convert the returned UTC instant back and risk the off-by-one.
    const enteredExpires = expires.value || null;

    void adminCreateInviteCode(body)
      .then((created) => {
        form.reset();
        // The POST returns a plain InviteCode; the table wants the admin shape.
        // Derive the two extra fields locally rather than re-fetching the whole
        // table: a brand-new code is always active, and its scope is what was
        // just submitted.
        onCreated({
          ...created,
          groupName:
            created.groupId === null
              ? null
              : (groups.find((g) => g.groupId === created.groupId)?.name ?? null),
          status: 'active',
          expiresOn: enteredExpires,
        });
      })
      .catch((err: unknown) => {
        error.textContent =
          err instanceof ApiClientError ? err.message : 'Could not create that code.';
        error.hidden = false;
      })
      .finally(() => submit.removeAttribute('disabled'));
  });

  return form;
}
